import { UserImporterSettings, UserImporterTableColumn } from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { UsersImporterSaveUsersRequest } from '../../../models/requests/users-importer';

const FEATURE_KEY = 'usersImporter';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const importUsers = createAsyncThunk(
  'usersimporter/import',
  async (data: UsersImporterSaveUsersRequest, thunkAPI) => {
    const response = await API.membersImport().saveUsers(data);
    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

interface UsersImporterSummary {
  numberOfInserts: number;
  errors: {
    user: string;
    column: number;
    errors: string[];
  }[];
}

export interface UsersImporterState {
  activeStep: number;
  data: any[];
  settings: UserImporterSettings;
  headers: UserImporterTableColumn[];
  summary: UsersImporterSummary;
}

const initialState: UsersImporterState = {
  activeStep: 0,
  data: [],
  settings: {
    sendMail: false,
  },
  headers: [],
  summary: {
    numberOfInserts: 0,
    errors: [],
  },
};

export const usersImporterSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    incrementStep: (state, action: PayloadAction<number>) => {
      if (state.activeStep < action.payload - 1) {
        state.activeStep += 1;
      }
    },
    decrementStep: (state) => {
      if (state.activeStep > 0) {
        state.activeStep -= 1;
      }
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },
    setHeaders: (state, action: PayloadAction<UserImporterTableColumn[]>) => {
      state.headers = action.payload;
      state.data = [];
    },
    setData: (state, action: PayloadAction<any[]>) => {
      state.data = action.payload;
    },
    setSettings: (state, action: PayloadAction<UserImporterSettings>) => {
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      importUsers.fulfilled,
      (
        state: UsersImporterState,
        action: PayloadAction<UsersImporterSummary>
      ) => {
        state.summary = action.payload;
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const usersImporterReducer = usersImporterSlice.reducer;
export const usersImporterActions = usersImporterSlice.actions;

const state = (rootState: any): UsersImporterState => rootState[FEATURE_KEY];

export const usersImporterActiveStepSelector = createSelector(
  state,
  (state) => {
    return state.activeStep;
  }
);

export const usersImporterHeadersSelector = createSelector(state, (state) => {
  return state.headers;
});

export const usersImporterDataSelector = createSelector(state, (state) => {
  return state.data;
});

export const usersImporterSettingsSelector = createSelector(state, (state) => {
  return state.settings;
});

export const usersImporterSummarySelector = createSelector(state, (state) => {
  return state.summary;
});
